import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import {
  Modal,
  message,
  Form,
  Input,
  Select,
  Popconfirm,
  Tooltip,
  ModalProps,
} from "@gw/web-basic-components";
import { ReactComponent as DeleteSvg } from "./assets/deleteRed.svg";
import useRequest, { apis } from "./use-request";
import { getBankIcon } from "./utils";
import DotLabel from "./dot-label";
import { ReactComponent as QuestionMark } from "./assets/question-mark.svg";
import plus from "./assets/plus.png";
import { RecordData } from "./dto";

interface IProps extends ModalProps {
  // 公司id
  id?: string;
  options?: { value: number; label: string }[];
  callback: (data: any) => void;
}

const typeOptions = [
  { value: 2, label: "项目公司" },
  { value: 1, label: "子公司" },
];
const typeOption = [
  { value: 2, label: "项目公司" },
  { value: 1, label: "子公司" },
  { value: 0, label: "母公司" },
];
// 手机号码格式校验
const phonePattern = (_, value, callback) => {
  const phoneRegex = /^(1[3-9]\d{9}|0\d{2,3}-\d{7,8})$/;
  if (!phoneRegex.test(value) && value) {
    callback("手机号码格式错误");
  }
  callback();
};
/**
 * 新增-编辑公司弹窗
 */
const AddCompanyModal = ({
  id,
  title,
  options,
  callback,
  open,
  okText,
  cancelText,
  ...rest
}: IProps) => {
  const [form] = Form.useForm();
  const [limit, setLimit] = useState<number>(5);
  const [recordData, setRecordData] = useState<RecordData>(null);
  const flag = useMemo(() => id !== undefined, [id]);
  const [{ loading }, create] = useRequest(apis.compony.create);
  const [{ loading: loadingEdit }, update] = useRequest(apis.compony.update);
  const [, fetchLimit] = useRequest<any, number>(apis.compony.limit);
  const [{ loading: loadingRecord }, fetchRecord] = useRequest<any, RecordData>(
    apis.compony.companyDetail
  );
  const addRef = useRef(null);
  const bankValue = Form.useWatch("bankList", form) ?? [];
  const onSubmit = useCallback(
    (e) => {
      if (!flag) {
        create(e).then((res) => {
          message.success("新建成功");
          if (callback) {
            callback(res);
          }
        });
      } else {
        update({ ...e, id: id }).then(() => {
          message.success("编辑成功");
          if (callback) {
            callback(id);
          }
        });
      }
    },
    [flag, id]
  );

  const getLimit = async () => {
    const res = await fetchLimit();
    setLimit(res);
  };

  const getRecordData = async () => {
    const res = await fetchRecord({ id: id });
    form.setFieldsValue({ ...res });
    setRecordData(res);
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setRecordData(null);
    } else if (id) {
      getRecordData();
      getLimit();
    }
  }, [open, id]);

  const addBankCard = () => {
    const bankList = form.getFieldValue("bankList") ?? [];
    // 检查所有已添加的银行信息是否都通过校验
    const allValid = bankList.every((bank) => {
      const openingBankValid =
        bank?.openingBank && /^.{1,50}$/.test(bank.openingBank);
      const bankAccountValid =
        bank?.bankAccount && /^[0-9]{9,25}$/.test(bank.bankAccount);
      return openingBankValid && bankAccountValid;
    });

    if (allValid) {
      if (addRef.current) {
        addRef.current();
      }
    } else {
      message.warning("请先补全并校验通过已添加的银行信息");
    }
  };

  const dealInfo = () => {
    const bankList = form.getFieldValue("bankList") ?? [];
    const allOk = bankList.every((item: any) => {
      return item?.openingBank && item?.bankAccount;
    });
    if (!allOk) {
      message.warning("有银行信息缺失");
    }
  };

  const realOption = options ?? typeOptions;

  const handleTitle = (customTitle: string | React.ReactNode) => {
    if (customTitle) return customTitle;
    return flag ? "编辑公司" : "新增公司";
  };

  return (
    <Modal
      {...rest}
      open={open}
      centered
      loading={loading || loadingEdit || loadingRecord}
      title={handleTitle(title)}
      onOk={() => {
        dealInfo();
        form.submit();
      }}
      okText={okText ?? "确定"}
      cancelText={cancelText ?? "取消"}
    >
      <div className={"wbuc-add-company-contain"}>
        <Form form={form} onFinish={onSubmit}>
          <div className={"wbuc-add-company-grid"}>
            <Form.Item
              label="公司名称"
              name={"name"}
              rules={[{ required: true, message: "请填写公司名称" }]}
            >
              <Input
                size="large"
                placeholder="请输入公司名称"
                disabled={recordData?.relTypes?.some((i) => i === 0)}
                maxLength={100}
              />
            </Form.Item>
            <Form.Item
              label="纳税人识别税号"
              name={"taxNumber"}
              validateFirst={true}
              rules={[
                { required: true, message: "请填写纳税人识别税号" },
                { pattern: /^[0-9A-Z]*$/, message: "仅支持输入数字和大写字母" },
              ]}
            >
              <Input
                size="large"
                placeholder="请输入纳税人识别税号"
                maxLength={25}
              />
            </Form.Item>
            <Form.Item
              label="关联类型"
              name={"relTypes"}
              rules={[{ required: true, message: "请选择关联类型" }]}
            >
              <Select
                mode="multiple"
                size="large"
                options={
                  recordData?.relTypes?.some((i) => i === 0)
                    ? typeOption
                    : realOption
                }
                placeholder="选择关联类型"
                disabled={recordData?.relTypes?.some((i) => i === 0)}
              />
            </Form.Item>
            <Form.Item label="公司地址" name={"address"}>
              <Input
                size="large"
                placeholder="请输入公司地址"
                maxLength={100}
              />
            </Form.Item>
            <Form.Item
              label="电话号码"
              name={"phone"}
              validateTrigger="onChange"
              rules={[{ validator: phonePattern }]}
            >
              <Input size="large" placeholder="请输入电话号码" />
            </Form.Item>
          </div>

          <div className={"wbuc-add-company-dot-title"}>
            <DotLabel title="银行账号" />
            <Tooltip
              title={<span>最多配置{limit}个银行账号</span>}
              placement="bottomLeft"
            >
              <QuestionMark className={"wbuc-add-company-question"} />
            </Tooltip>
          </div>

          <Form.List name="bankList">
            {(fields, { add, remove }) => {
              addRef.current = add;
              return (
                <div className={"wbuc-add-company-grid"}>
                  {fields.map(({ key, name }) => {
                    const openingBankValue = bankValue[name]?.openingBank;
                    return (
                      <div key={key} className={"wbuc-add-company-card"}>
                        <div className={"wbuc-add-company-header"}>
                          <div className={"wbuc-add-company-avater"}>
                            {/*  */}
                            <img
                              className={"wbuc-add-company-bank-icon"}
                              src={getBankIcon(openingBankValue)}
                              alt=""
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <Form.Item
                              required
                              key={key}
                              name={[name, "openingBank"]}
                              rules={[
                                { required: true, message: "请输入开户银行" },
                              ]}
                            >
                              <Input
                                bordered={false}
                                placeholder="请输入开户银行"
                                size="small"
                                maxLength={50}
                              />
                            </Form.Item>
                            <Form.Item
                              rules={[
                                {
                                  pattern: /^[0-9]{9,25}$/,
                                  message: "请输入9-25的数字",
                                },
                                { required: true, message: "请输入银行账号" },
                              ]}
                              name={[name, "bankAccount"]}
                            >
                              <Input
                                bordered={false}
                                placeholder="请输入银行账号"
                                size="small"
                                onFocus={() => {
                                  // 触发开户银行的校验
                                  form.validateFields([
                                    ["bankList", name, "openingBank"],
                                  ]);
                                }}
                              />
                            </Form.Item>
                          </div>
                        </div>
                        <div className={"wbuc-add-company-footer"}>
                          <Popconfirm
                            title="是否删除此银行卡？"
                            onConfirm={() => remove(name)}
                            okText="确认"
                            cancelText="取消"
                            placement="top"
                          >
                            <DeleteSvg
                              className={"wbuc-add-company-delete-icon"}
                            />
                          </Popconfirm>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }}
          </Form.List>
        </Form>
        {bankValue?.length < limit && (
          <div onClick={addBankCard} className={"wbuc-add-company-add-card"}>
            <img className={"wbuc-add-company-plus"} src={plus} />
            添加银行卡
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddCompanyModal;
